import { useAuth } from "../auth/useAuth";
import { hasModuleAccess, getAccessibleModules, MODULES } from "../config/moduleConfig";

/**
 * Hook to check if user has specific permission(s) or module access
 * @returns {Object} - Object with permission checking functions
 */
export function usePermission() {
  const { permissions } = useAuth();

  /**
   * Check if user has a specific permission
   * @param {string} permissionCode - The permission code to check (e.g., "products.view")
   * @returns {boolean} - True if user has the permission
   */
  const hasPermission = (permissionCode) => {
    if (!permissions || permissions.length === 0) return false;
    return permissions.includes(permissionCode);
  };

  /**
   * Check if user has any of the specified permissions
   * @param {string[]} permissionCodes - Array of permission codes
   * @returns {boolean} - True if user has at least one of the permissions
   */
  const hasAnyPermission = (...permissionCodes) => {
    if (!permissions || permissions.length === 0) return false;
    return permissionCodes.some((code) => permissions.includes(code));
  };

  /**
   * Check if user has all of the specified permissions
   * @param {string[]} permissionCodes - Array of permission codes
   * @returns {boolean} - True if user has all permissions
   */
  const hasAllPermissions = (...permissionCodes) => {
    if (!permissions || permissions.length === 0) return false;
    return permissionCodes.every((code) => permissions.includes(code));
  };

  /**
   * Check if user has access to a specific module
   * @param {string} module - Module key from MODULES
   * @returns {boolean} - True if user has any permission in the module
   */
  const hasModule = (module) => {
    return hasModuleAccess(permissions, module);
  };

  // Convenience getters for specific modules
  const hasInventoryAccess = hasModuleAccess(permissions, MODULES.INVENTORY);
  const hasSecurityAccess = hasModuleAccess(permissions, MODULES.SECURITY);
  const hasResultsAccess = hasModuleAccess(permissions, MODULES.RESULTS);
  const hasIncomeAccess = hasModuleAccess(permissions, MODULES.INCOME);
  const accessibleModules = getAccessibleModules(permissions);

  return {
    // Permission checks
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    // Module checks
    hasModule,
    hasInventoryAccess,
    hasSecurityAccess,
    hasResultsAccess,
    hasIncomeAccess,
    accessibleModules,
    // Raw permissions
    permissions,
    // Module constants for convenience
    MODULES,
  };
}
